/**
 * ivNicknames — the IV nickname generator (and its OCR-facing decoder).
 *
 * WHAT THIS SOLVES
 * ----------------
 * Trainers coming from Poke Genie rename their Pokémon so the IVs read at a
 * glance in GO itself: "Rayquaza¹⁵¹⁴¹⁵", "Medicham⑮⑮⑮", "Mewtwo⁹⁸". GO's
 * nickname box is 12 characters, so the community leans on one-character
 * glyphs (superscript digits, circled numbers) to pack a stat line beside a
 * species name. This module is BOTH halves of that loop:
 *
 *   1. GENERATE — a template of tokens (species, IV triple, IV percent, CP,
 *      level, league percents...) renders a record + its derived stats into a
 *      nickname that fits GO's 12-character limit, ready to copy and paste
 *      into the in-game rename box. The species token is elastic: it shrinks
 *      to whatever budget the stat glyphs leave.
 *   2. DECODE — decodeStyledGlyphs() maps every glyph the generator can emit
 *      back to plain ASCII. The caught-page name OCR runs incoming display
 *      names through it (myPokemonOcr.speciesIdFromStyledName), so the very
 *      names this module writes, and the ones Poke Genie users already have,
 *      resolve to their species instead of jamming species detection.
 *      Whatever alphabet the generator learns, the reader learns in the same
 *      commit; the two can never drift.
 *
 * The 12-character limit is counted in Unicode code points ([...s].length).
 * Every glyph this module emits is BMP, so code points, UTF-16 units and
 * GO's own count agree.
 *
 * Templates persist per device in nicknameTemplateStore.ts.
 */

/** Digit rendering style for numeric tokens. */
export type NicknameDigitStyle =
  | "plain"
  | "superscript"
  | "subscript"
  | "circled"
  | "circledDark";

export const NICKNAME_DIGIT_STYLES: readonly NicknameDigitStyle[] = [
  "plain",
  "superscript",
  "subscript",
  "circled",
  "circledDark",
];

/** GO's nickname length limit, in Unicode code points. */
export const GO_NICKNAME_MAX = 12;

const SUPERSCRIPT_DIGITS = ["⁰", "¹", "²", "³", "⁴", "⁵", "⁶", "⁷", "⁸", "⁹"] as const;
const SUBSCRIPT_DIGITS = ["₀", "₁", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"] as const;
const CIRCLED_DIGITS = ["⓪", "①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨"] as const;
/** Whole-number circled glyphs 10-20: one character for a whole IV value. */
const CIRCLED_TENS = ["⑩", "⑪", "⑫", "⑬", "⑭", "⑮", "⑯", "⑰", "⑱", "⑲", "⑳"] as const;
/**
 * Negative-circled (dark) glyphs 0-15: the second common IV alphabet, and the
 * accent half of the maxed-stat highlight (a dark ⓯ beside white ⑭s). One
 * glyph per whole IV, like CIRCLED_TENS. Shared with DECODE_MAP below, so the
 * OCR reader learns this alphabet in the same table the generator writes.
 */
const NEG_CIRCLED = ["⓿", "❶", "❷", "❸", "❹", "❺", "❻", "❼", "❽", "❾", "❿", "⓫", "⓬", "⓭", "⓮", "⓯"] as const;

/**
 * League markers, per digit style (2026-08-31 owner report: "the circled
 * option on G/U/M doesn't actually deliver a circled letter" — the bestLeague
 * token ignored `style` outright and always emitted the superscript letter).
 *
 * Circled styles get the BMP circled capitals. There IS a negative-circled
 * capital set (U+1F150…) but it is ASTRAL, which would break this module's
 * one-code-point-per-glyph invariant against GO's 12-character count and does
 * not render in the game, so circledDark shares the light circled letter.
 * Subscript has no capital letters in Unicode at all, so it falls back to
 * plain rather than emitting a stray lowercase.
 */
const LEAGUE_LETTERS: Record<
  NicknameDigitStyle,
  Record<"little" | "great" | "ultra" | "master", string>
> = {
  plain: { little: "L", great: "G", ultra: "U", master: "M" },
  superscript: { little: "ᴸ", great: "ᴳ", ultra: "ᵁ", master: "ᴹ" },
  subscript: { little: "L", great: "G", ultra: "U", master: "M" },
  circled: { little: "Ⓛ", great: "Ⓖ", ultra: "Ⓤ", master: "Ⓜ" },
  circledDark: { little: "Ⓛ", great: "Ⓖ", ultra: "Ⓤ", master: "Ⓜ" },
};

/** Default marks for the flag parts; a token's `text` overrides them. */
export const FLAG_GLYPHS = {
  lucky: "✦",
  shadow: "☠",
  purified: "☀",
  eliteMove: "★",
} as const;

/**
 * Half-level tail for the circled styles. A plain ".5" beside a run of circled
 * glyphs reads as a typo and costs TWO of the twelve code points; the vulgar
 * fraction is one glyph, is BMP, and renders in GO (2026-08-31 owner ask).
 */
const HALF_LEVEL_GLYPH = "½";

/**
 * The league marker in one digit style. Exported so the Naming editor can show
 * a real sample on its style buttons instead of a number, and so nothing but
 * this table decides what a league letter looks like.
 */
export function styleLeagueMark(
  style: NicknameDigitStyle,
  league: "little" | "great" | "ultra" | "master" = "great",
): string {
  return LEAGUE_LETTERS[style][league];
}

/** Render one non-negative integer in a digit style. */
export function styleNumber(n: number, style: NicknameDigitStyle): string {
  if (!Number.isFinite(n) || n < 0) return "";
  const whole = Math.round(n);
  if (style === "circled") {
    // A whole IV (0-15) is a single glyph; anything bigger goes per-digit.
    if (whole <= 9) return CIRCLED_DIGITS[whole];
    if (whole <= 20) return CIRCLED_TENS[whole - 10];
    return String(whole)
      .split("")
      .map((d) => CIRCLED_DIGITS[Number(d)])
      .join("");
  }
  if (style === "circledDark") {
    // Same shape as circled: single glyph through 15, per-digit above.
    if (whole <= 15) return NEG_CIRCLED[whole];
    return String(whole)
      .split("")
      .map((d) => NEG_CIRCLED[Number(d)])
      .join("");
  }
  const table =
    style === "superscript" ? SUPERSCRIPT_DIGITS : style === "subscript" ? SUBSCRIPT_DIGITS : null;
  if (!table) return String(whole);
  return String(whole)
    .split("")
    .map((d) => table[Number(d)])
    .join("");
}

/** Render a level (half steps allowed): whole levels drop the ".0". */
function styleLevel(level: number, style: NicknameDigitStyle): string {
  const whole = Math.floor(level);
  const half = level - whole >= 0.5;
  const head = styleNumber(whole, style);
  if (!half) return head;
  // Circled levels take the vulgar fraction (㊵½): one code point instead of
  // two, and it sits with the glyph run instead of breaking it. Every other
  // style keeps the plain ".5" tail — no superscript dot exists, and a
  // mixed-script tail still decodes cleanly.
  if (style === "circled" || style === "circledDark") return `${head}${HALF_LEVEL_GLYPH}`;
  return `${head}.5`;
}

/* ── Template model ──────────────────────────────────────────────────────── */

export type NicknameTokenKind =
  // Base
  | "species"
  | "dexNumber"
  | "cp"
  | "hp"
  | "level"
  | "lucky"
  | "shadow"
  | "purified"
  | "ivTriple"
  | "ivPercent"
  | "stars"
  | "gender"
  | "eliteMove"
  // Destiny (lib/bestPotential): what this IV triple becomes
  | "targetSpecies"
  | "targetCp"
  | "targetHp"
  | "bestLeague"
  | "targetPct"
  | "bestRank"
  | "dustCost"
  | "candyCost"
  | "xlCost"
  // PvP, one league at a time
  | "littleMark"
  | "littlePct"
  | "littleRank"
  | "greatMark"
  | "greatPct"
  | "greatRank"
  | "ultraMark"
  | "ultraPct"
  | "ultraRank"
  | "masterMark"
  | "masterPct"
  | "masterRank"
  | "bestPct"
  | "text";

export interface NicknameToken {
  kind: NicknameTokenKind;
  /** Digit style for numeric tokens; ignored elsewhere. Default "plain". */
  style?: NicknameDigitStyle;
  /**
   * Literal text, kind "text" only (separators like " " or "·"); for the
   * flag kinds (lucky / shadow / purified / eliteMove) an optional glyph
   * override for the default mark.
   */
  text?: string;
  /**
   * Rank kinds only (2026-09-03 owner ask): print a "#" before the number,
   * "#472" beside the bare "472" option.
   */
  hash?: boolean;
  /** Separator between the three IVs, kind "ivTriple" only. Default "". */
  sep?: string;
  /**
   * Maxed-stat highlight, kind "ivTriple" with a circled style only: a 15
   * renders as the dark ⓯ while everything below stays white-circled, so the
   * maxed stats pop ("Medicham⓯⑭⓯"). Ignored for non-circled styles, where
   * mixing alphabets would just read as noise.
   */
  maxDark?: boolean;
  /**
   * The same highlight INVERTED (2026-08-31 owner ask): a 15 stays white-
   * circled while everything below goes dark ("Medicham⑮❿⑮"), so the eye
   * lands on the maxed stats as the light ones and the mixed stats recede.
   * Circled styles only, same as maxDark; when both are set maxDark wins,
   * because it is the older stored flag and a template that carries both is
   * malformed rather than meaningful.
   */
  maxLight?: boolean;
  /**
   * Species length cap, kind "species" only (2026-08-29, from Poke Genie's
   * Name2/Name3/Name5 tokens): every name truncates to the SAME width, so
   * the stat glyphs line up when a box is sorted by name ("Char⓯⑭⓯" /
   * "Venu❾⑬⑭"). Unset = flex to whatever the budget allows. The remaining
   * budget still wins when it is smaller than the cap.
   */
  maxLen?: number;
}

export interface NicknameTemplate {
  tokens: NicknameToken[];
}

/** Everything a template can print, already derived (see factsForRecord). */
export interface NicknameFacts {
  speciesName: string | null;
  ivAtk: number | null;
  ivDef: number | null;
  ivHp: number | null;
  /** 0-100 (one decimal ok; rendered rounded). */
  ivPercent: number | null;
  cp: number | null;
  /** 1-51 in half steps. */
  level: number | null;
  /** GO appraisal stars 0-3 (the UI's hundo 4★ is display-only). */
  stars: number | null;
  greatPct: number | null;
  ultraPct: number | null;
  masterPct: number | null;
  /**
   * The Pokémon's BEST case percent (bestOptionOf: the top of IV percent and
   * every enterable league percent, 2026-08-30). The "one number that
   * matters" token for PvP-minded naming.
   */
  bestPct: number | null;
  /**
   * Full 1-4096 league rank of this IV combo for its species (1 = the best
   * possible spread under the cap), or null when unranked or not computed.
   * Ranks cost a per-species sweep, so adapters only fill these in when the
   * template actually asks (templateWantsRanks).
   */
  greatRank: number | null;
  ultraRank: number | null;
  /**
   * The destiny tokens (2026-08-30 ask): a Charmander destined to be a Great
   * League Charizard names itself for what it WILL be, so it never needs a
   * rename after the evolve + power-up. bestRank is the lowest full rank
   * this IV triple reaches across the whole evolution line and every
   * enterable league; bestLeague names that league; targetSpeciesName the
   * form that gets there (see lib/bestPotential). Adapter-filled only when
   * the template asks (templateWantsPotential) — the sweep is not free.
   */
  bestRank: number | null;
  bestLeague: "great" | "ultra" | "master" | null;
  targetSpeciesName: string | null;
  gender: "male" | "female" | null;
  // ── 2026-09-03 additions (the Naming page's Base / Destiny / PvP groups) ──
  /** National dex number. */
  dexNumber?: number | null;
  hpMax?: number | null;
  isLucky?: boolean;
  isShadow?: boolean;
  isPurified?: boolean;
  /** One of the Pokémon's known moves is an Elite / Legacy move. */
  hasEliteMove?: boolean;
  littlePct?: number | null;
  littleRank?: number | null;
  masterRank?: number | null;
  /** The destiny target's CP / HP at its target level, and the percent the pick was made on. */
  targetCp?: number | null;
  targetHp?: number | null;
  targetPct?: number | null;
  /** Power-up cost from the current level to the destiny target. */
  dustCost?: number | null;
  candyCost?: number | null;
  xlCost?: number | null;
}

/** Whether any token in the template needs the league ranks computed. */
export function templateWantsRanks(template: NicknameTemplate): boolean {
  return template.tokens.some(
    (t) =>
      t.kind === "greatRank" ||
      t.kind === "ultraRank" ||
      t.kind === "masterRank" ||
      t.kind === "littleRank" ||
      t.kind === "littlePct",
  );
}

/** Whether any token needs the cross-evolution best-potential sweep. */
const POTENTIAL_KINDS: ReadonlySet<NicknameTokenKind> = new Set<NicknameTokenKind>([
  "bestRank",
  "bestLeague",
  "targetSpecies",
  "targetCp",
  "targetHp",
  "targetPct",
  "dustCost",
  "candyCost",
  "xlCost",
]);

export function templateWantsPotential(template: NicknameTemplate): boolean {
  return template.tokens.some((t) => POTENTIAL_KINDS.has(t.kind));
}

/** Whether any token needs the species move pool (elite / legacy lookup). */
export function templateWantsMoves(template: NicknameTemplate): boolean {
  return template.tokens.some((t) => t.kind === "eliteMove");
}

/** The record + derived slices the adapter needs (structural, no store dep). */
export function factsForRecord(
  record: {
    speciesName: string | null;
    ivAtk: number | null;
    ivDef: number | null;
    ivHp: number | null;
    cp: number | null;
    appraisalStars: number | null;
    gender: "male" | "female" | null;
  },
  derived: {
    level: number | null;
    ivPercent: number | null;
    greatPct: number | null;
    ultraPct: number | null;
    masterPct: number | null;
  },
  /** Caller-computed extras: league ranks (templateWantsRanks), the
   *  best-option percent (bestOptionOf, needs the full derived shape), and
   *  the destiny facts (templateWantsPotential → lib/bestPotential). */
  extra?: {
    greatRank?: number | null;
    ultraRank?: number | null;
    bestPct?: number | null;
    bestRank?: number | null;
    bestLeague?: "great" | "ultra" | "master" | null;
    targetSpeciesName?: string | null;
    littlePct?: number | null;
    littleRank?: number | null;
    masterRank?: number | null;
    targetCp?: number | null;
    targetHp?: number | null;
    targetPct?: number | null;
    dustCost?: number | null;
    candyCost?: number | null;
    xlCost?: number | null;
    hasEliteMove?: boolean;
  },
): NicknameFacts {
  const r = record as typeof record & {
    speciesId?: number | null;
    hpMax?: number | null;
    isLucky?: boolean;
    isShadow?: boolean;
    isPurified?: boolean;
  };
  return {
    dexNumber: r.speciesId ?? null,
    hpMax: r.hpMax ?? null,
    isLucky: r.isLucky === true,
    isShadow: r.isShadow === true,
    isPurified: r.isPurified === true,
    hasEliteMove: extra?.hasEliteMove === true,
    littlePct: extra?.littlePct ?? null,
    littleRank: extra?.littleRank ?? null,
    masterRank: extra?.masterRank ?? null,
    targetCp: extra?.targetCp ?? null,
    targetHp: extra?.targetHp ?? null,
    targetPct: extra?.targetPct ?? null,
    dustCost: extra?.dustCost ?? null,
    candyCost: extra?.candyCost ?? null,
    xlCost: extra?.xlCost ?? null,
    speciesName: record.speciesName,
    ivAtk: record.ivAtk,
    ivDef: record.ivDef,
    ivHp: record.ivHp,
    ivPercent: derived.ivPercent,
    cp: record.cp,
    level: derived.level,
    stars: record.appraisalStars,
    greatPct: derived.greatPct,
    ultraPct: derived.ultraPct,
    masterPct: derived.masterPct,
    bestPct: extra?.bestPct ?? null,
    greatRank: extra?.greatRank ?? null,
    ultraRank: extra?.ultraRank ?? null,
    bestRank: extra?.bestRank ?? null,
    bestLeague: extra?.bestLeague ?? null,
    targetSpeciesName: extra?.targetSpeciesName ?? null,
    gender: record.gender,
  };
}

export interface RenderedNickname {
  /** The nickname, at most GO_NICKNAME_MAX code points. */
  name: string;
  /** Code-point length of `name`. */
  length: number;
  /**
   * True when fixed (non-species) parts alone exceeded the limit and the
   * output had to be hard-truncated — the template is too greedy.
   */
  overflow: boolean;
  /** Token kinds skipped because the record lacks the fact (unread IVs...). */
  missing: NicknameTokenKind[];
}

const codePoints = (s: string): string[] => [...s];

/**
 * Render one token to its glyph string, or null when the fact is missing.
 * The species token is handled by renderNickname (it flexes to the budget).
 */
/** A rank in the token's style, with the optional "#" in front. */
function rank(value: number | null, token: NicknameToken): string | null {
  if (value == null) return null;
  return (token.hash ? "#" : "") + styleNumber(value, token.style ?? "plain");
}

function renderFixedToken(token: NicknameToken, facts: NicknameFacts): string | null {
  const style = token.style ?? "plain";
  switch (token.kind) {
    case "text":
      return token.text ?? "";
    case "ivTriple": {
      if (facts.ivAtk == null || facts.ivDef == null || facts.ivHp == null) return null;
      const sep = token.sep ?? "";
      // Both highlights force the two circled alphabets against each other,
      // so the base `style` only decides whether the highlight applies at all.
      const circled = style === "circled" || style === "circledDark";
      const highlight: "dark" | "light" | null = !circled
        ? null
        : token.maxDark === true
          ? "dark"
          : token.maxLight === true
            ? "light"
            : null;
      return [facts.ivAtk, facts.ivDef, facts.ivHp]
        .map((v) => {
          if (highlight === "dark") return styleNumber(v, v === 15 ? "circledDark" : "circled");
          if (highlight === "light") return styleNumber(v, v === 15 ? "circled" : "circledDark");
          return styleNumber(v, style);
        })
        .join(sep);
    }
    case "ivPercent":
      return facts.ivPercent == null ? null : styleNumber(Math.round(facts.ivPercent), style);
    case "cp":
      return facts.cp == null ? null : styleNumber(facts.cp, style);
    case "level":
      return facts.level == null ? null : styleLevel(facts.level, style);
    case "stars":
      return facts.stars == null ? null : `${styleNumber(facts.stars, style)}★`;
    case "greatPct":
      return facts.greatPct == null ? null : styleNumber(Math.round(facts.greatPct), style);
    case "ultraPct":
      return facts.ultraPct == null ? null : styleNumber(Math.round(facts.ultraPct), style);
    case "masterPct":
      return facts.masterPct == null ? null : styleNumber(Math.round(facts.masterPct), style);
    case "bestPct":
      return facts.bestPct == null ? null : styleNumber(Math.round(facts.bestPct), style);
    case "greatRank":
      return rank(facts.greatRank, token);
    case "ultraRank":
      return rank(facts.ultraRank, token);
    case "bestRank":
      return rank(facts.bestRank, token);
    case "masterRank":
      return rank(facts.masterRank ?? null, token);
    case "littleRank":
      return rank(facts.littleRank ?? null, token);
    case "littlePct":
      return facts.littlePct == null ? null : styleNumber(Math.round(facts.littlePct), style);
    case "dexNumber":
      return facts.dexNumber == null ? null : styleNumber(facts.dexNumber, style);
    case "hp":
      return facts.hpMax == null ? null : styleNumber(facts.hpMax, style);
    // Flags print their mark when true and NOTHING when false: an absent
    // lucky mark is the fact, not a missing one.
    case "lucky":
      return facts.isLucky ? token.text ?? FLAG_GLYPHS.lucky : "";
    case "shadow":
      return facts.isShadow ? token.text ?? FLAG_GLYPHS.shadow : "";
    case "purified":
      return facts.isPurified ? token.text ?? FLAG_GLYPHS.purified : "";
    case "eliteMove":
      return facts.hasEliteMove ? token.text ?? FLAG_GLYPHS.eliteMove : "";
    case "littleMark":
      return LEAGUE_LETTERS[token.style ?? "superscript"].little;
    case "greatMark":
      return LEAGUE_LETTERS[token.style ?? "superscript"].great;
    case "ultraMark":
      return LEAGUE_LETTERS[token.style ?? "superscript"].ultra;
    case "masterMark":
      return LEAGUE_LETTERS[token.style ?? "superscript"].master;
    case "targetCp":
      return facts.targetCp == null ? null : styleNumber(facts.targetCp, style);
    case "targetHp":
      return facts.targetHp == null ? null : styleNumber(facts.targetHp, style);
    case "targetPct":
      return facts.targetPct == null ? null : styleNumber(Math.round(facts.targetPct), style);
    // Costs: dust in thousands ("120k"), candy and XL as plain counts. A
    // Pokémon already at its target costs nothing, which prints as 0.
    case "dustCost":
      return facts.dustCost == null
        ? null
        : facts.dustCost >= 1000
          ? `${styleNumber(Math.round(facts.dustCost / 1000), style)}k`
          : styleNumber(facts.dustCost, style);
    case "candyCost":
      return facts.candyCost == null ? null : styleNumber(facts.candyCost, style);
    case "xlCost":
      return facts.xlCost == null ? null : styleNumber(facts.xlCost, style);
    case "bestLeague":
      // One league letter IN THE TOKEN'S OWN STYLE. This used to hard-code the
      // superscript letter and ignore `style` completely, so picking "Circled"
      // on a league part changed nothing (2026-08-31 owner report).
      // DECODE_MAP reads every alphabet here back.
      //
      // Defaults to SUPERSCRIPT, not plain: superscript was this token's only
      // behaviour for its whole life, so every stored template that carries a
      // league part without an explicit style means the superscript letter.
      // Reading `token.style` here would silently rewrite them all to bare G.
      return facts.bestLeague == null
        ? null
        : LEAGUE_LETTERS[token.style ?? "superscript"][facts.bestLeague];
    case "gender":
      return facts.gender == null ? null : facts.gender === "male" ? "♂" : "♀";
    case "species":
    case "targetSpecies":
      return ""; // flexed below; never reached via this path
  }
}

/** Species-like tokens: name text that flexes into the leftover budget. */
function isSpeciesLike(kind: NicknameTokenKind): boolean {
  return kind === "species" || kind === "targetSpecies";
}

function speciesLikeName(kind: NicknameTokenKind, facts: NicknameFacts): string | null {
  return kind === "targetSpecies" ? facts.targetSpeciesName : facts.speciesName;
}

/**
 * Render a template against one Pokémon's facts. Fixed tokens render first;
 * the species token (first one, if present) then flexes into whatever budget
 * remains, truncated mid-name when it must be ("Dragoni¹⁵¹⁴¹⁵"). Tokens whose
 * fact is unread are skipped and reported in `missing` so the UI can say
 * "scan the appraisal to fill this in" instead of printing junk.
 */
export function renderNickname(
  template: NicknameTemplate,
  facts: NicknameFacts,
): RenderedNickname {
  const missing: NicknameTokenKind[] = [];
  const parts: Array<{ token: NicknameToken; text: string }> = [];
  // The FIRST species-like token (species or targetSpecies) is the flex slot;
  // any later one renders fixed, truncated by its own maxLen.
  let speciesIndex = -1;
  for (const token of template.tokens) {
    if (isSpeciesLike(token.kind) && speciesIndex === -1) {
      speciesIndex = parts.length;
      parts.push({ token, text: "" });
      continue;
    }
    if (isSpeciesLike(token.kind)) {
      const name = speciesLikeName(token.kind, facts);
      if (name == null) {
        missing.push(token.kind);
        continue;
      }
      const text = codePoints(name)
        .slice(0, token.maxLen ?? Number.POSITIVE_INFINITY)
        .join("");
      if (text.length > 0) parts.push({ token, text });
      continue;
    }
    const text = renderFixedToken(token, facts);
    if (text == null) {
      missing.push(token.kind);
      continue;
    }
    if (text.length > 0) parts.push({ token, text });
  }

  const fixedLen = parts.reduce((n, p) => n + codePoints(p.text).length, 0);
  if (speciesIndex !== -1) {
    const flexToken = parts[speciesIndex].token;
    const flexName = speciesLikeName(flexToken.kind, facts);
    if (flexName == null) {
      missing.push(flexToken.kind);
    } else {
      const budget = GO_NICKNAME_MAX - fixedLen;
      const maxLen = flexToken.maxLen;
      const cap = Math.min(Math.max(0, budget), maxLen ?? Number.POSITIVE_INFINITY);
      const species = codePoints(flexName).slice(0, cap).join("");
      parts[speciesIndex] = { ...parts[speciesIndex], text: species };
    }
  }

  let name = parts.map((p) => p.text).join("");
  let overflow = false;
  const cps = codePoints(name);
  if (cps.length > GO_NICKNAME_MAX) {
    name = cps.slice(0, GO_NICKNAME_MAX).join("");
    overflow = true;
  }
  // A separator-only tail (species truncated to nothing before a trailing
  // space, say) reads as a typo in GO; trim it.
  name = name.replace(/\s+$/g, "").replace(/^\s+/g, "");
  return { name, length: codePoints(name).length, overflow, missing };
}

/* ── Presets ─────────────────────────────────────────────────────────────── */

export interface NicknamePreset {
  id: string;
  label: string;
  /** One-line "what it looks like" blurb for the picker. */
  blurb: string;
  /**
   * Destiny band: names the Pokémon for what it BECOMES rather than what it
   * is. These lead the list and the Naming page tints them Nexus green.
   */
  destiny?: boolean;
  template: NicknameTemplate;
}

/**
 * The built-in presets, in two bands (2026-08-31 owner direction):
 *
 *   1. DESTINY (the first three, `destiny: true`): name a Pokémon for what it
 *      BECOMES, so it never needs a rename after the evolve + power-up. The
 *      Naming page tints this band Nexus green.
 *   2. The classics (the next five): one preset per KIND of fact, not per
 *      style. Style variants of the IV triple were retired here, since every
 *      part is restyleable in place once a preset is picked.
 *
 * Across the eight, the presets deliberately SHOW OFF the alphabets rather
 * than all reaching for superscript (2026-08-31 owner ask): raised, dropped,
 * circled, dark circled and the maxed-stat highlight each appear at least
 * once, so the picker doubles as the menu of what a name can look like. Every
 * one of those alphabets is in DECODE_MAP, so variety costs the scanner
 * nothing.
 */
export const NICKNAME_PRESETS: readonly NicknamePreset[] = [
  {
    // 2026-08-30 ask: name the Charmander for the Charizard it will become,
    // so it never needs a rename after the evolve + power-up.
    id: "destiny",
    label: "Named for its destiny",
    blurb: "Target species, circled league, raised rank.",
    destiny: true,
    template: {
      tokens: [
        { kind: "targetSpecies" },
        { kind: "bestLeague", style: "circled" },
        { kind: "bestRank", style: "superscript" },
      ],
    },
  },
  {
    // 2026-08-31 owner ask: circled IVs with the MIXED stats dark, so the
    // maxed ones read as the light glyphs and the rest recede.
    id: "destiny-ivs",
    label: "Destiny + IVs",
    blurb: "What it becomes, maxed stats picked out.",
    destiny: true,
    template: {
      tokens: [
        { kind: "targetSpecies" },
        { kind: "ivTriple", style: "circled", maxLight: true },
      ],
    },
  },
  {
    id: "destiny-percent",
    label: "Destiny + best %",
    blurb: "What it becomes, with a dropped percent.",
    destiny: true,
    template: {
      tokens: [
        { kind: "targetSpecies" },
        { kind: "bestLeague", style: "superscript" },
        { kind: "bestPct", style: "subscript" },
      ],
    },
  },
  {
    id: "triple-super",
    label: "IVs, superscript",
    blurb: "The classic: species then the triple.",
    template: {
      tokens: [{ kind: "species" }, { kind: "ivTriple", style: "superscript" }],
    },
  },
  {
    id: "percent-super",
    label: "IV percent",
    blurb: "Species plus the rounded percent, circled.",
    template: {
      tokens: [{ kind: "species" }, { kind: "ivPercent", style: "circled" }],
    },
  },
  {
    id: "pvp-great",
    label: "Great League",
    blurb: "Circled G, then its GL percent.",
    template: {
      tokens: [
        { kind: "species" },
        { kind: "text", text: "Ⓖ" },
        { kind: "greatPct", style: "circled" },
      ],
    },
  },
  {
    id: "level-triple",
    label: "Level + IVs",
    blurb: "Subscript level, superscript triple.",
    template: {
      tokens: [
        { kind: "species" },
        { kind: "level", style: "subscript" },
        { kind: "ivTriple", style: "superscript" },
      ],
    },
  },
  {
    id: "gl-rank",
    label: "Great League rank",
    blurb: "Its 1 to 4096 GL rank, in dark circles.",
    template: {
      tokens: [
        { kind: "species" },
        { kind: "text", text: "Ⓖ" },
        { kind: "greatRank", style: "circledDark" },
      ],
    },
  },
];

/**
 * Preset ids that used to ship and no longer do. A device still holding one
 * keeps its TOKENS (the store persists those separately) and simply reads as
 * a custom layout, so a retired preset can never blank a trainer's naming.
 */
export const RETIRED_NICKNAME_PRESET_IDS: readonly string[] = [
  "triple-circled",
  "hundo-pop",
];

export const DEFAULT_NICKNAME_PRESET_ID = "triple-super";

export function presetById(id: string): NicknamePreset | undefined {
  return NICKNAME_PRESETS.find((p) => p.id === id);
}

/* ── Decoding (the OCR half of the loop) ─────────────────────────────────── */

/**
 * Every styled glyph the generator can emit, mapped back to ASCII, plus the
 * superscript league letters presets use. Built once; decodeStyledGlyphs
 * walks code points so surrogate-free lookups stay O(n).
 */
const DECODE_MAP: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  SUPERSCRIPT_DIGITS.forEach((g, i) => (map[g] = String(i)));
  SUBSCRIPT_DIGITS.forEach((g, i) => (map[g] = String(i)));
  CIRCLED_DIGITS.forEach((g, i) => (map[g] = String(i)));
  CIRCLED_TENS.forEach((g, i) => (map[g] = String(10 + i)));
  // Parenthesized + double-circled lookalikes OCR sometimes swaps in.
  const PARENTHESIZED = ["⑴", "⑵", "⑶", "⑷", "⑸", "⑹", "⑺", "⑻", "⑼", "⑽", "⑾", "⑿", "⒀", "⒁", "⒂"];
  PARENTHESIZED.forEach((g, i) => (map[g] = String(i + 1)));
  const DOUBLE_CIRCLED = ["⓵", "⓶", "⓷", "⓸", "⓹", "⓺", "⓻", "⓼", "⓽", "⓾"];
  DOUBLE_CIRCLED.forEach((g, i) => (map[g] = String(i + 1)));
  // Negative-circled (black) digits: now also a GENERATOR alphabet
  // ("circledDark" + the maxed-stat highlight), same shared table.
  NEG_CIRCLED.forEach((g, i) => (map[g] = String(i)));
  // Superscript letters the presets emit (league markers).
  map["ᴳ"] = "G";
  map["ᵁ"] = "U";
  map["ᴹ"] = "M";
  map["ᴸ"] = "L";
  // 2026-08-31: the bestLeague token now honours its digit style, so the
  // CIRCLED capitals are a generator alphabet too. Learned in the same commit
  // that taught the generator to write them — the invariant this whole module
  // rests on. Their lowercase circled twins are here as OCR lookalikes only.
  map["Ⓖ"] = "G";
  map["Ⓤ"] = "U";
  map["Ⓜ"] = "M";
  map["Ⓛ"] = "L";
  map["ⓖ"] = "G";
  map["ⓤ"] = "U";
  map["ⓜ"] = "M";
  map["ⓛ"] = "L";
  // The half-level tail on a circled level ("㊵½").
  map["½"] = ".5";
  // 2026-08-31 (scanner "doesn't detect the circles / sub / super text"):
  // more lookalikes seen from Vision on the styled alphabets. Fullwidth
  // digits (CJK-font substitution), the dingbat negative/sans circled runs
  // (swapped in for our negative-circled digits), and modifier-letter
  // primes it reads superscript ¹ as. Deliberately NOT mapped: plain O/o
  // for a circle — "Oddish" must never become "0ddish".
  const FULLWIDTH = ["０", "１", "２", "３", "４", "５", "６", "７", "８", "９"];
  FULLWIDTH.forEach((g, i) => (map[g] = String(i)));
  const DINGBAT_NEG_CIRCLED = ["➊", "➋", "➌", "➍", "➎", "➏", "➐", "➑", "➒", "➓"];
  DINGBAT_NEG_CIRCLED.forEach((g, i) => (map[g] = String(i + 1)));
  const DINGBAT_SANS_CIRCLED = ["➀", "➁", "➂", "➃", "➄", "➅", "➆", "➇", "➈", "➉"];
  DINGBAT_SANS_CIRCLED.forEach((g, i) => (map[g] = String(i + 1)));
  map["′"] = "1";
  map["ⁿ"] = "n";
  return map;
})();

/**
 * Replace every styled glyph with its ASCII reading ("Rayquaza¹⁵¹⁴¹⁵" becomes
 * "Rayquaza151415", "⑮⑭⑮" becomes "151415"). Text without styled glyphs
 * passes through untouched, so this is safe to run on every OCR read.
 */
export function decodeStyledGlyphs(text: string): string {
  let out = "";
  for (const cp of text) out += DECODE_MAP[cp] ?? cp;
  return out;
}

/**
 * The comparison form of a nickname (2026-08-31): OCR flattens the styled
 * glyphs to plain ASCII ("Zubat²²²" reads as "Zubat222"), so a scanned
 * nickname and a generated one are the SAME name whenever their decoded,
 * space-collapsed, case-folded forms match. Every "is this already named /
 * the same individual?" check must compare through this, never with raw
 * `===` — raw comparison is why a correctly-renamed Pokémon kept being
 * re-queued for renaming.
 */
export function nicknameCompareKey(text: string | null | undefined): string {
  if (!text) return "";
  return decodeStyledGlyphs(text).replace(/\s+/g, "").toLowerCase();
}
